<template>
  <div class="ot-schedule-create">
    <v-breadcrumbs :items="items" class="px-0"></v-breadcrumbs>
    <v-alert v-if="errors" type="error" dense dismissible class="mb-3">{{ errors }}</v-alert>
    <v-form @submit.prevent="dataSave()" ref="form" v-model="valid" lazy-validation>
      <v-row>
        <v-col cols="12" md="5" lg="4">
          <v-card :loading="loadingstatus" class="schedule-form-card">
            <v-card-title class="py-3 d-flex flex-wrap align-center">
              <span>OT Schedule</span>
              <v-spacer></v-spacer>
              <v-chip
                  v-if="submitData.status"
                  small
                  label
                  :color="statusChipColor"
                  text-color="white"
                  class="ml-2"
              >
                {{ submitData.status }}
              </v-chip>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-4">
              <div class="section-title">Patient</div>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                      v-model="submitData.patient_id"
                      outlined
                      dense
                      label="Patient ID"
                      hide-details="auto"
                      :readonly="coreFieldsLocked"
                      :disabled="coreFieldsLocked"
                      v-on:change="getPatient()"
                  ></v-text-field>
                  <div class="patient-meta mt-1 mb-2">
                    <v-chip
                        v-if="error"
                        label
                        class="ma-1"
                        small
                        color="red"
                        text-color="white"
                    >
                      {{ error }}
                    </v-chip>
                    <v-chip
                        v-if="submitData.patient_name"
                        label
                        class="ma-1"
                        small
                        color="success"
                        text-color="white"
                    >
                      {{ submitData.patient_name }}
                    </v-chip>
                    <v-chip v-if="submitData.patient_phone" label class="ma-1" small>
                      {{ submitData.patient_phone }}
                    </v-chip>
                    <v-chip v-if="submitData.patient_sex" label class="ma-1" small>
                      {{ submitData.patient_sex }}
                    </v-chip>
                    <v-chip v-if="submitData.patient_dob" label class="ma-1" small>
                      {{ submitData.patient_dob }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                      v-model="submitData.surgeon_id"
                      item-text="dr_name"
                      item-value="id"
                      :items="doctors"
                      label="Surgeon Name"
                      outlined
                      dense
                      hide-details="auto"
                      :readonly="coreFieldsLocked"
                      :disabled="coreFieldsLocked"
                      v-on:change="getReports()"
                      :loading="loadingstatus"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                      v-model="submitData.referral_doctor_id"
                      :items="doctors"
                      item-text="dr_name"
                      item-value="id"
                      required
                      label="Referral Doctor"
                      outlined
                      dense
                      hide-details="auto"
                      @focusin="toggleReadonly"
                      @focusout="toggleReadonly"
                      :readonly="true"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>
              <div class="section-title">Operation</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                      v-model="submitData.operation_id"
                      :items="operations"
                      item-text="name"
                      item-value="id"
                      required
                      label="Operation Name"
                      outlined
                      dense
                      hide-details="auto"
                      :readonly="coreFieldsLocked"
                      :disabled="coreFieldsLocked"
                      v-on:change="getReports(), getOperationID()"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.operation_subhead"
                      label="Operation Subhead"
                      dense
                      outlined
                      hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu
                      v-model="menu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model="submitData.operation_date"
                          label="Operation Date"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                          hide-details="auto"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                        v-model="submitData.operation_date"
                        @input="menu = false"
                        @click:date="dateSelect"
                        :min="nowDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu
                      ref="menu"
                      v-model="menu2"
                      :close-on-content-click="false"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model="submitData.starting_time"
                          label="Starting Time"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                          hide-details="auto"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                        v-if="menu2"
                        v-model="submitData.starting_time"
                        full-width
                        @click:minute="dateSelect"
                    ></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" v-bind:class="isOperationEyeBlock">
                  <v-radio-group
                      row
                      v-model="submitData.eye_type"
                      :rules="rules.required"
                      required
                      class="mt-0"
                      hide-details="auto"
                  >
                    <template v-slot:label>
                      <div><strong>Operation Eye</strong></div>
                    </template>
                    <v-radio label="Left Eye" value="Left Eye"></v-radio>
                    <v-radio label="Right Eye" value="Right Eye"></v-radio>
                    <v-radio label="Both Eye" value="Both Eye"></v-radio>
                    <v-radio label="N/A" value="--"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>
              <div class="section-title">IOL / Room</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                      label="IOL"
                      v-model="submitData.iol_id"
                      :items="iol_list"
                      item-text="name"
                      item-value="id"
                      dense
                      outlined
                      hide-details="auto"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.iol_power"
                      label="IOL Power"
                      dense
                      outlined
                      hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                      v-model="submitData.ot_no"
                      :items="ot_rooms"
                      item-text="name"
                      item-value="name"
                      item-key="name"
                      label="OT Room"
                      outlined
                      dense
                      hide-details="auto"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.cabine"
                      label="Cabin"
                      dense
                      outlined
                      hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.amount"
                      label="OT Amount"
                      dense
                      outlined
                      hide-details="auto"
                      type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.due_amount"
                      label="Due Amount"
                      dense
                      outlined
                      hide-details="auto"
                      type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                      v-model="submitData.note"
                      outlined
                      dense
                      rows="2"
                      label="Note"
                      hide-details="auto"
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>
              <div class="section-title">Family Contact</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.family_member_name"
                      label="Family Member Name"
                      outlined
                      dense
                      hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.family_contact_no"
                      label="Contact Number"
                      outlined
                      dense
                      hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                      v-model="submitData.family_member_relation"
                      label="Relation with Patient"
                      outlined
                      dense
                      hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" v-if="submitData.counselor">
                  <v-chip color="green" text-color="white" small label>
                    Counselor: {{ submitData.counselor.full_name }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="schedule-actions pa-3">
              <v-btn
                  small
                  depressed
                  color="primary"
                  :disabled="buttonSubmit || scheduleLocked"
                  type="submit"
                  class="ma-1"
              >
                Save
              </v-btn>
              <v-btn
                  small
                  depressed
                  color="primary"
                  :disabled="buttonUpdate"
                  type="submit"
                  class="ma-1"
              >
                Update
              </v-btn>
              <v-btn
                  small
                  depressed
                  color="success"
                  :disabled="buttonRegistration || scheduleLocked"
                  type="button"
                  class="ma-1"
                  @click="ot_registration"
              >
                Registration
              </v-btn>
              <v-btn
                  v-if="isRegistered && registrationNo"
                  small
                  depressed
                  color="info"
                  type="button"
                  class="ma-1"
                  @click="goToRegistrationDetails"
              >
                Registration Details
              </v-btn>
              <v-btn
                  v-if="!scheduleLocked"
                  small
                  depressed
                  color="warning"
                  @click="dialog = true"
                  :disabled="buttonCancel"
                  type="button"
                  class="ma-1"
              >
                Cancel
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" md="7" lg="8">
          <div class="d-flex flex-wrap align-center mb-3">
            <v-btn small color="info" class="ma-1" v-on:click="to_schedule_list">
              Back to schedule list
            </v-btn>
            <v-btn
                v-if="isRegistered && registrationNo"
                class="ma-1"
                small
                depressed
                color="success"
                type="button"
                @click="goToRegistrationDetails"
            >
              Go to Registration #{{ registrationNo }}
            </v-btn>
            <v-btn
                class="ma-1"
                small
                depressed
                color="warning"
                :disabled="OtDischargeCertificate"
                type="button"
                @click="ot_discharge_certificate"
            >
              Discharge Certificate
            </v-btn>
          </div>
          <v-card>
            <v-card-title class="py-3">OT Counseling Patient</v-card-title>
            <v-data-table
                :headers="headers"
                :items="desserts"
                class="elevation-0"
                dense
                mobile-breakpoint="0"
                :loading="loadingstatus"
                loading-text="Loading... Please wait"
                disable-pagination
            >
              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                        @click="getOTSchedule(item)"
                        dark
                        v-bind="attrs"
                        v-on="on"
                        class="ma-1"
                        color="primary"
                        label
                        outlined
                        small
                    >
                      <v-icon small>mdi-plus</v-icon>
                    </v-chip>
                  </template>
                  <span>Set OT Schedule</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
    <template>
      <div class="text-center">
        <v-dialog v-model="dialog" persistent width="500">
          <v-card>
            <v-card-title class="text-h5 grey lighten-2">
              OT Schedule Cancel: {{ submitData.id }}
            </v-card-title>
            <v-card-text>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                  <tr>
                    <td>Schedule Date</td>
                    <td>:</td>
                    <td class="">{{ submitData.operation_date }}</td>
                  </tr>
                  <tr>
                    <td>Patient ID</td>
                    <td>:</td>
                    <td class="">{{ submitData.patient_id }}</td>
                  </tr>
                  <tr>
                    <td>Patient Name</td>
                    <td>:</td>
                    <td class="">{{ submitData.patient_name }}</td>
                  </tr>
                  <tr>
                    <td>Status</td>
                    <td>:</td>
                    <td class="">{{ submitData.status }}</td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-text-field
                  v-model="cancelData.patient_id"
                  label="Patient ID"
                  outlined
                  dense
              ></v-text-field>
              <v-textarea
                  outlined
                  v-model="cancelData.description"
                  name="input-7-4"
                  label="Description"
                  dense
              ></v-textarea>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="warning" text @click="dialog = false"> Close</v-btn>
              <v-btn color="primary" text @click="scheduleCancel()"> Submit</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </template>
  </div>
</template>

<style scoped>
.ot-schedule-create {
  width: 100%;
}
.schedule-form-card {
  height: 100%;
}
.section-title {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
}
.schedule-actions {
  flex-wrap: wrap;
  justify-content: flex-start;
}
.patient-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
@media (max-width: 960px) {
  .schedule-form-card {
    margin-bottom: 16px;
  }
}
</style>
<script>
import atshq from "../../../../js/setup";

export default {
  data() {
    return {
      dialog: false,
      isOperationEyeBlock: 'd-block',
      desserts: [],
      headers: [
        {
          text: "Operation Name",
          value: "operation.name",
        },
        {
          text: "Operation Subhead",
          value: "operation_subhead",
        },
        {
          text: "Right Eye",
          value: "variant",
        },
        {
          text: "Left Eye",
          value: "variant_1",
        },
        {text: "Amount", value: "ot_amount"},
        {text: "Note", value: "comments"},

        // { text: "Date", value: "date" },
        {text: "Doctor", value: "doctor.dr_name"},
        {text: "Action", value: "actions", sortable: false},
      ],
      cancelData: {
        schedule_id: null,
        patient_id: null,
        description: null,
        user_id: localStorage.getItem("userId"),
      },
      iol_list: [],
      submitData: {
        patient_id: null,
        surgeon_id: null,
        patient_name: null,
        patient_phone: null,
        patient_sex: null,
        patient_dob: null,
        referral_doctor_id: null,
        operation_id: null,
        operation_subhead: null,
        eye_type: null,
        operation_date: new Date().toISOString().substr(0, 10),
        starting_time: null,

        iol_id: null,
        iol_power: null,
        amount: null,
        due_amount: null,
        ot_no: null,
        cabine: null,
        note: null,

        family_member_name: null,
        family_contact_no: null,
        family_member_relation: null,

        counselor_id: null,
        counseling_id: null,
        counselor: {},
      },
      url_param: {
        counseling_id: this.$route.query.counseling_id,
        patient_id: this.$route.query.patient_id,
        schedule_id: this.$route.query.schedule_id ? this.$route.query.schedule_id : 0,
      },
      nowDate: new Date().toISOString().substr(0, 10),
      errors: null,
      error: null,
      ot_rooms: [],
      menu2: false,
      valid: true,
      doctors: [],
      operations: [],
      OtDischargeCertificate: true,
      buttonUpdate: true,
      buttonRegistration: true,
      buttonCancel: true,
      coreFieldsLocked: false,
      scheduleLocked: false,
      isRegistered: false,
      registrationNo: null,

      returnUrl: "",
      search: "",
      loadingstatus: true,
      loading: false,
      menu: false,
      column: null,
      patient_info: {},
      rules: {
        required: [(value) => !!value || "Required."],
        nameRules: [
          (v) => !!v || "Patient Name is required",
          (v) => (v && v.length <= 100) || "Name must be less than 100 characters",
        ],
      },

      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "OT Schedule",
          disabled: true,
          href: "OT Schedule",
        },
      ],
    };
  },
  // Fetches posts when the component is created.
  created() {
    this.patient_id = this.url_param.patient_id;
    if (this.url_param.schedule_id) {
      this.OtDischargeCertificate = false;
      this.buttonUpdate = false;
      this.buttonRegistration = false;
      this.buttonCancel = false;
    }

    this.initialize();
    this.getDoctors();
    this.getOperations();
    this.getOtRooms();
    this.getIOL();
    this.checkSurgeonIdType();
    this.loadingstatus = false;
  },
  watch: {
    menu(val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = "YEAR"));
    },
  },
  computed: {
    buttonSubmit: function () {
      return this.desserts.length && !this.url_param.counseling_id ? true : false;
      // return this.buttonUpdate ? false : true;
    },
    statusChipColor() {
      const status = (this.submitData && this.submitData.status) || "";
      if (status === "Registration") return "success";
      if (status === "OT Cancel") return "error";
      if (status === "active") return "primary";
      return "grey";
    },
  },

  methods: {
    getIOL() {
      atshq
          .get("/lens/lens-active-list")
          .then((response) => {
            this.iol_list = response.data.lensProducts;
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    initialize() {
      if (this.url_param.schedule_id) {
        this.getSchedule(this.url_param.schedule_id);
      }

      if (this.url_param.counseling_id) {
        atshq
            .get("/ot-module/ot-counsiling/" + this.url_param.counseling_id)
            .then((response) => {
              this.submitData.surgeon_id =parseInt(response.data.data.doctor_id);
              this.submitData.referral_doctor_id = parseInt(response.data.data.referral_doctor_id);
              this.submitData.operation_id = parseInt(response.data.data.operation_id);
              this.submitData.operation_subhead = response.data.data.operation_subhead;
              this.submitData.operation_date = response.data.data.followup_date;
              this.submitData.amount = response.data.data.ot_amount;
              this.submitData.iol_id = parseInt(response.data.data.iol);
              this.submitData.iol_power = response.data.data.iol_power;
              this.submitData.cabine = response.data.data.cabine;
              this.submitData.ot_no = response.data.data.ot_no;
              this.submitData.counselor = response.data.data.counselor;
              this.submitData.counselor_id = parseInt(response.data.data.counselor.id);
              this.submitData.counseling_id = parseInt(this.url_param.counseling_id);
              this.checkSurgeonIdType();
              this.submitData.eye_type = response.data.data.variant
                  ? response.data.data.variant
                  : response.data.data.variant_1;

            })
            .catch((e) => {
              this.errors = true;
              this.errorMessage = e;
            });
      }

      if (this.url_param.patient_id) {
        this.submitData.patient_id = this.url_param.patient_id;
        this.getPatient();
        this.getReports();
      }
    },
    toggleReadonly(event) {
      event.preventDefault()
      if (event.target.getAttribute('readonly') == 'readonly') {
        event.target.removeAttribute('readonly')
      } else {
        event.target.setAttribute('readonly', 'readonly')
      }
    },
    checkSurgeonIdType() {
      const id = this.submitData.surgeon_id;
      if (id === null) {
        this.result = "Surgeon ID is null";
      } else if (Array.isArray(id)) {
        this.result = "Surgeon ID is an array";
      } else if (typeof id === "object") {
        this.result = "Surgeon ID is an object";
      } else {
        this.result = `Surgeon ID is of type: ${typeof id}`;
      }
    },
    getSchedule(schedule_id) {
      atshq
          .get("/ot-module/schedule/" + schedule_id)
          .then((response) => {
            this.submitData = response.data.data;
            this.submitData.surgeon_id =parseInt(response.data.data.surgeon_id);
            this.submitData.referral_doctor_id = parseInt(response.data.data.referral_doctor_id);
            this.submitData.operation_id = parseInt(response.data.data.operation_id);
            this.submitData.iol_id = parseInt(response.data.data.iol_id);
            this.submitData.counselor = response.data.data.counselor;

            this.scheduleLocked = !!response.data.data.schedule_locked || !!response.data.data.has_invoice;
            this.coreFieldsLocked = this.scheduleLocked;
            this.isRegistered = !!(response.data.data.is_registered || response.data.data.status === "Registration");
            this.registrationNo = response.data.data.registration_no || null;

            if (this.submitData.status == "Registration") {
              this.buttonRegistration = true;
              this.buttonCancel = true;
              this.isRegistered = true;
            }
            if (this.submitData.status == "OT Cancel") {
              this.buttonRegistration = true;
              this.buttonUpdate = true;
              this.buttonCancel = true;
              this.coreFieldsLocked = true;
              this.scheduleLocked = true;
            }
            if (this.scheduleLocked) {
              this.buttonRegistration = true;
              this.buttonCancel = true;
              this.coreFieldsLocked = true;
            }
            this.getPatient();
            this.getReports();
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    getPatient() {
      atshq
          .get("patients/patients/get/" + this.submitData.patient_id)
          .then((response) => {
            if (response.data.data) {
              this.error = null;
              this.submitData.patient_name = response.data.data.patient_name;
              this.submitData.patient_phone = response.data.data.patient_phone;
              this.submitData.patient_sex = response.data.data.patient_sex;
              this.submitData.patient_dob = response.data.data.patient_dob;
              this.submitData.referral_doctor_id = response.data.data.referral_doctor_id

            } else {
              this.error = "Invalid Patient ID. Please Input Correct ID.";
              this.$noty.error(this.error);
              this.submitData.patient_name = null;
              this.submitData.patient_phone = null;
              this.submitData.patient_sex = null;
              this.submitData.patient_dob = null;

            }
            this.getReports();
            this.loadingstatus = false;
            this.isLoading = false;
          })
          .catch((e) => {
            console.log(e);
            // this.errors.push(e);
          });
    },
    getDoctors() {
      atshq
          .get("/doctors")
          .then((response) => {
            this.doctors = response.data.data;
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    getOTs() {
      atshq
          .get("/ot-module/ot")
          .then((response) => {
            this.doctors = response.data.data;
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    getOtRooms() {
      atshq
          .get("/ot-module/ot-room")
          .then((response) => {
            this.ot_rooms = response.data.data;
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    getOperations() {
      atshq
          .get("/operations")
          .then((response) => {
            this.operations = response.data.data;
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    getOperationID() {
      if (this.submitData.operation_id == 288) {
        this.isOperationEyeBlock = 'd-none'
        this.submitData.eye_type = '--'
      } else {
        this.isOperationEyeBlock = 'd-block'
        this.submitData.eye_type = null
      }
    },
    getReports() {
      atshq
          .get(
              "ot-module/ot-counsiling-get-patient?patient_id=" +
              this.submitData.patient_id +
              "&doctor_id=" +
              this.submitData.surgeon_id +
              "&operation_id=" +
              this.submitData.operation_id
          )
          .then((response) => {
            // JSON responses are automatically parsed.
            this.loadingstatus = false;
            this.desserts = response.data.data;
          })
          .catch((e) => {
            this.errors.push(e);
          });
    },
    save(date) {
      this.$refs.menu.save(date);
    },
    getAge(getage) {
      let age = parseInt(getage);
      if (age > 99) {
        this.patient_data.patient_age = 99;
      }
      age = new Date(new Date().setFullYear(new Date().getFullYear() - age));
      var months = age.getMonth() + 1;
      age = age.getFullYear() + "-" + months + "-" + age.getDate();

      this.patient_data.patient_dob = age;
    },
    getOTSchedule(item) {
      window.location.href =
          "/ot-module/schedule-create?patient_id=" +
          item.patient_id +
          "&counseling_id=" +
          item.id;
    },
    to_schedule_list() {
      window.location.href = "/ot-module/schedule";
    },
    goToRegistrationDetails() {
      if (!this.registrationNo) {
        this.$noty.error("Registration number not found.");
        return;
      }
      window.location.href = "/ot-registration/" + this.registrationNo;
    },
    dataSave() {
      if (this.$refs.form.validate()) {
        if (this.submitData.id) {
          let submitUrl = "ot-module/schedule/" + this.submitData.id;
          atshq
              .put(submitUrl, {
                formData: this.submitData,
                user_id: localStorage.getItem("userId"),
              })
              .then((response) => {
                // JSON responses are automatically parsed.
                this.loadingstatus = false;
                if (response.data.status === false) {
                  this.$noty.error(response.data.messages || "Update blocked.");
                  return;
                }
                this.$noty.success(response.data.messages);
                window.location.href =
                    "/ot-module/schedule-create?schedule_id=" + response.data.data.id;
              })
              .catch((e) => {
                // this.errors.push(e);
                this.$noty.error("Oops, something went wrong!");
                this.$noty.error(" " + e);
              });
        } else {
          if (this.scheduleLocked) {
            this.$noty.error("Save is disabled because an invoice has been created.");
            return;
          }
          let submitUrl = "ot-module/schedule";
          atshq
              .post(submitUrl, {
                formData: this.submitData,
                user_id: localStorage.getItem("userId"),
              })
              .then((response) => {
                // JSON responses are automatically parsed.
                this.loadingstatus = false;
                this.$noty.success(response.data.messages);
                window.location.href =
                    "/ot-module/schedule-create?schedule_id=" + response.data.data.id;
              })
              .catch((e) => {
                this.$noty.error(" " + e.response.data.message);
              });
        }
      } else {
        // console.log("error");
        this.$noty.error("Oops, something went wrong!");
      }
    },
    ot_registration() {
      //   window.location.href =
      //     "/ot-module/schedule-create?schedule_id=" + item.id;
      // },
      var result = confirm("Are you Sure?");
      if (result) {
        //Logic to delete the item
        // console.log(item);
        atshq
            .post("ot-module/schedule-ot-registration", {
              data: {id: this.url_param.schedule_id},
              user_id: localStorage.getItem("userId"),
            })
            .then((response) => {
              this.loadingstatus = false;
              if (response.data.status == false) {
                this.$noty.error("Oops, something went wrong!");
                this.$noty.error(response.data.messages);
              } else {
                this.$noty.success(response.data.messages);
                window.location.href = "/ot-registration/" + response.data.data;
                this.getReports();
              }
            })
            .catch((e) => {
              this.errors.push(e);
            });
      }
    },
    scheduleCancel() {
      if (confirm("are you sure?")) {
        if (this.cancelData.patient_id != this.submitData.patient_id) {
          this.$noty.error("Oops, Please input valid Patient ID.");
          return false;
        }
        this.cancelData.schedule_id = this.url_param.schedule_id;
        atshq
            .post("ot-module/schedule-cancel", {
              data: this.cancelData,
              user_id: localStorage.getItem("userId"),
            })
            .then((response) => {
              this.$noty.success(response.data.message);
              this.dialog = false;
              this.cancelData.patient_id = "";
              this.cancelData.description = "";
              this.initialize();
            }).catch((e) => {
          this.$noty.error(e.response.data.message);
          this.initialize();
        })
      }
    },
    ot_discharge_certificate() {
      window.location.href = "/ot-discharge-certificate/" + this.url_param.schedule_id;
    },
    dateSelect() {
      return true;
    },
  },
};
</script>
